/* oxlint-disable no-inner-declarations */
import type { DeepPartial } from 'react-hook-form';
import { merge } from 'lodash';

import type { UpdatableType } from '../ts/updatable-type';

export namespace Optimistic {
  const prefix = 'optimistic-';

  export function isOptimisticId(id: string): boolean {
    return id.startsWith(prefix);
  }
  export function id(): string {
    return `${prefix}${Date.now()}`;
  }

  export function makeUpdate<O>() {
    return function optimistic(update: (previous: O) => O) {
      return function (previous: O | undefined) {
        if (!previous) {
          return undefined;
        }
        return update(previous);
      };
    };
  }

  // INFO: use only to clear nulls value on the optimistic update
  // DO NOT USE THIS ELSEWHERE
  function deepMapValues(
    obj: object,
    mapper: (value: unknown) => void,
  ): object {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, mapper(value)]),
    );
  }

  // INFO: use only to clear nulls value on the optimistic update
  // DO NOT USE THIS ELSEWHERE
  function isPlainObject(value: unknown): value is object {
    return Object.prototype.toString.call(value) === '[object Object]';
  }

  // INFO: use only to clear nulls value on the optimistic update
  // DO NOT USE THIS ELSEWHERE
  function cleanNulls(updated: object): object {
    return deepMapValues(updated, function (value) {
      if (value === null) {
        return undefined;
      }
      if (isPlainObject(value)) {
        return cleanNulls(value);
      }

      return value;
    });
  }

  export function updateEntity<O>(
    previous: O,
    update: DeepPartial<UpdatableType<O>>,
  ): O {
    return cleanNulls(merge({}, previous, update)) as O;
  }

  export function updateItem<O>(
    arr: O[],
    id: (item: O) => boolean,
    update: DeepPartial<UpdatableType<O>>,
  ): O[] {
    return arr.map(function (item) {
      if (!id(item)) {
        return item;
      }
      return updateEntity(item, update);
    });
  }

  export function deleteItem<O>(arr: O[], id: (item: O) => boolean): O[] {
    return arr.filter((item) => !id(item));
  }
}
